import { useParams } from 'react-router-dom';

import { useT } from '@/core/I18nProvider';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { NotFound } from '../not-found';
import { getOnePager } from './one-pager-content';
import { OnePager } from './OnePager';

import type { ReactElement } from 'react';

/**
 * AF-138 — `/one-pagers/:icp` route wrapper. Resolves the `:icp` param to a
 * one-pager; an unknown slug renders the 404 page (no silent fallback).
 *
 * AF-136 — when the slug resolves, this route owns the page head (per-archetype
 * title + description + canonical via the iter66 `useSeoMeta` hook). When it
 * doesn't, the wrapped `NotFound` owns its own head — we don't set seo-meta
 * for an invalid slug (would emit a misleading canonical for a non-existent
 * page).
 */
export function OnePagerPage(): ReactElement {
  const { t } = useT();
  const { icp } = useParams<{ icp: string }>();
  const data = icp !== undefined ? getOnePager(icp) : undefined;

  // Always call the hook (Rules of Hooks); when there's no resolved one-pager
  // we render `NotFound` below and the managed tags this hook injected get
  // removed on the next render (the hook returns no DOM, and React unmounts
  // the effect cleanly on re-render); but for a clean signal, only inject
  // when we actually have a one-pager to describe.
  useLocalizedSeoMeta(
    data !== undefined
      ? {
          title: t(data.titleKey),
          description: t(data.summaryKey),
          path: `/one-pagers/${data.slug}`,
        }
      : {
          title: t('onepagers.index.metaTitle'),
          description: t('onepagers.page.notFoundMetaDescription'),
          path: '/one-pagers',
        },
  );

  if (data === undefined) {
    return <NotFound />;
  }
  return <OnePager data={data} />;
}
